import { state } from '../state.js';
import * as API from '../api.js';
import { UI_COPY } from '../config/uiCopy.js';
import {
    openCreateMosaicForEdit,
    refreshHistoryEmptyState,
    refreshSettingsVisibility,
    requestWorkflowScroll,
    updateHistoryCollapsedPreview,
} from '../workflow_selection.js';
import { clearSelectedSourcePhoto } from '../source_photos.js';
import { openConfirmModal } from '../modal_manager.js';
import { showToast } from './toast.js';
import { showEmpty } from '../result_viewer.js';

let _historyRenderedKeys = new Set();
let _historyExpiryTickerId = null;
const EXPIRING_SOON_WINDOW_S_FALLBACK = 48 * 60 * 60;
const EXPIRY_TICK_MS = 30 * 1000;

function _formatRemainingTime(seconds) {
    const safe = Math.max(0, Math.floor(Number(seconds) || 0));
    const days = Math.floor(safe / 86400);
    const hours = Math.floor((safe % 86400) / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${Math.max(1, minutes)}m`;
}

function _ensureHistoryExpiryTicker() {
    if (_historyExpiryTickerId) return;
    _historyExpiryTickerId = window.setInterval(() => {
        _refreshHistoryExpiryBadges();
    }, EXPIRY_TICK_MS);
}

function _stopHistoryExpiryTicker() {
    if (!_historyExpiryTickerId) return;
    window.clearInterval(_historyExpiryTickerId);
    _historyExpiryTickerId = null;
}

function _refreshHistoryExpiryBadges() {
    const items = Array.from(document.querySelectorAll('.history-item[data-expires-at-ts]'));
    if (items.length === 0) {
        _stopHistoryExpiryTicker();
        return;
    }
    const retention = state.historyRetention || {};
    const warningWindow = Number(retention.expiring_soon_window_s);
    const windowS = Number.isFinite(warningWindow) && warningWindow > 0
        ? Math.floor(warningWindow)
        : EXPIRING_SOON_WINDOW_S_FALLBACK;
    const nowS = Math.floor(Date.now() / 1000);
    let hasSoon = false;
    for (const item of items) {
        const expiresAt = Number(item.dataset.expiresAtTs || 0);
        const tile = item.querySelector('.history-tile');
        if (!tile || !Number.isFinite(expiresAt) || expiresAt <= 0) continue;
        let badge = tile.querySelector('.history-expiry-badge');
        if (!badge) {
            badge = document.createElement('div');
            badge.className = 'history-expiry-badge hidden';
            tile.appendChild(badge);
        }
        const remaining = Math.max(0, Math.floor(expiresAt - nowS));
        const isSoon = remaining <= windowS;
        item.classList.toggle('is-expiring-soon', isSoon);
        badge.classList.toggle('hidden', !isSoon);
        if (isSoon) {
            hasSoon = true;
            badge.textContent = remaining > 0
                ? `Deletes in ${_formatRemainingTime(remaining)}`
                : 'Deleting soon';
            badge.title = `Auto-delete around ${new Date(expiresAt * 1000).toLocaleString()}`;
        } else {
            badge.textContent = '';
            badge.removeAttribute('title');
        }
    }
    if (hasSoon || items.length > 0) {
        _ensureHistoryExpiryTicker();
    }
}

function _historyKey(job) {
    return job.manifest_key || job.img_url || `${job.job_id || ""}:${job.run_id || ""}`;
}

function _socialVideoBadgeState(job) {
    const social = (job && typeof job.social_video === 'object' && job.social_video) ? job.social_video : null;
    const status = String((social && social.status) || '').trim().toLowerCase();
    if (status === 'complete') {
        return { state: 'ready', label: 'Video ready' };
    }
    if (status === 'queued' || status === 'running') {
        return { state: 'generating', label: 'Video generating' };
    }
    if (status === 'failed') {
        return { state: 'failed', label: 'Video failed' };
    }
    return null;
}

function _applySocialVideoBadge(tile, job) {
    if (!tile) return;
    tile.classList.remove('has-social-video-badge');
    tile.querySelectorAll('.history-social-video-badge').forEach((badge) => badge.remove());
    const badgeState = _socialVideoBadgeState(job);
    if (!badgeState) return;
    const badge = document.createElement('div');
    badge.className = `history-social-video-badge is-${badgeState.state}`;
    badge.textContent = badgeState.label;
    tile.appendChild(badge);
    tile.classList.add('has-social-video-badge');
}

function _formatHistoryTimestamp(timestamp) {
    if (!timestamp) return "";
    const d = new Date(timestamp * 1000);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfTarget = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dayDiff = Math.round((startOfTarget - startOfToday) / 86400000);

    const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
    if (dayDiff === 0) return `Today ${timeFmt.format(d)}`;
    if (dayDiff === -1) return `Yesterday ${timeFmt.format(d)}`;

    const dateFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    return `${dateFmt.format(d)} ${timeFmt.format(d)}`;
}

function _formatHistoryTitle(timestamp) {
    if (!timestamp) return "";
    const d = new Date(timestamp * 1000);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString();
}

function _createHistoryTile(job, clickHandler, strip, card) {
    const key = _historyKey(job);
    const encodedKey = encodeURIComponent(String(key || ''));

    const item = document.createElement('div');
    item.className = 'trivia-thumb history-item';
    item.dataset.historyKey = encodedKey;
    item.dataset.jobId = String(job.job_id || '');
    item.setAttribute('role', 'button');
    item.tabIndex = 0;
    if (state.selectedHistoryJobId === job.job_id) {
        item.classList.add('is-selected');
    }

    const selectItem = () => {
        state.selectedHistoryJobId = job.job_id;
        state.selectedHistoryJob = job;
        const selected = strip.querySelectorAll('.history-item.is-selected');
        selected.forEach((el) => el.classList.remove('is-selected'));
        item.classList.add('is-selected');
        clickHandler(job);
    };
    item.onclick = selectItem;
    item.onkeydown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            selectItem();
        }
    };

    const timeStr = _formatHistoryTimestamp(job.timestamp);

    const tile = document.createElement('div');
    tile.className = 'history-tile';

    const expiresAtTs = Number(job.expires_at_ts || 0);
    if (Number.isFinite(expiresAtTs) && expiresAtTs > 0) {
        item.dataset.expiresAtTs = String(Math.floor(expiresAtTs));
        const expiryBadge = document.createElement('div');
        expiryBadge.className = 'history-expiry-badge hidden';
        tile.appendChild(expiryBadge);
    }

    const img = document.createElement('img');
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = job.thumb_url || job.img_url;
    _applySocialVideoBadge(tile, job);

    const finalizeRuns = Array.isArray(job.finalize_runs) ? job.finalize_runs : [];
    const readyFinalizeSizes = Array.from(
        new Set(
            finalizeRuns
                .filter((run) => String(run?.status || '') === 'complete')
                .map((run) => Number(run?.long_edge_px || 0))
                .filter((size) => Number.isFinite(size) && size > 1600)
        )
    ).sort((a, b) => a - b);
    tile.classList.toggle('has-finalize-badge', readyFinalizeSizes.length > 0);
    if (readyFinalizeSizes.length > 0) {
        const maxSize = readyFinalizeSizes[readyFinalizeSizes.length - 1];
        const badge = document.createElement('div');
        badge.className = 'history-finalize-badge';
        if (maxSize <= 3200) badge.textContent = 'Medium';
        else if (maxSize <= 8000) badge.textContent = 'Large';
        else badge.textContent = 'XLarge';
        badge.title = `Export ready: ${readyFinalizeSizes.join(', ')} px`;
        tile.appendChild(badge);
    }

    const draftMode = String(job?.draft_mode || job?.settings_snapshot?.draft_mode || '').toLowerCase();
    if (state.isAdmin && draftMode === 'legacy_highres') {
        const legacyBadge = document.createElement('div');
        legacyBadge.className = 'history-legacy-badge';
        legacyBadge.textContent = 'Legacy';
        legacyBadge.title = 'Generated with legacy high-res draft settings.';
        tile.appendChild(legacyBadge);
    }

    const delBtn = document.createElement('button');
    delBtn.type = 'button';
    delBtn.className = 'del-btn';
    delBtn.innerText = '✕';
    delBtn.title = UI_COPY.history.deleteTitle;
    delBtn.setAttribute('aria-label', UI_COPY.history.deleteTitle);
    delBtn.onclick = async (e) => {
        e.stopPropagation();
        const ok = await openConfirmModal({
            title: 'Delete mosaic?',
            message: UI_COPY.history.deleteConfirm,
            confirmText: 'Delete',
            danger: true,
        });
        if (!ok) return;
        try {
            const viewerContent = document.getElementById('viewerContent');
            const isResultVisible = Boolean(viewerContent && !viewerContent.classList.contains('hidden'));
            const wasDisplayed = isResultVisible && (
                state.selectedHistoryJobId === job.job_id ||
                state.jobId === job.job_id
            );
            const nextTile = item.nextElementSibling && item.nextElementSibling.classList.contains('history-item')
                ? item.nextElementSibling
                : null;
            const prevTile = item.previousElementSibling && item.previousElementSibling.classList.contains('history-item')
                ? item.previousElementSibling
                : null;

            const result = await API.deleteJob(job.job_id);
            if (result && result.delete_result && result.delete_result !== 'ok') {
                showToast('Delete incomplete', 'Cleanup finished with warnings. This mosaic might reappear until cleanup finishes.', 3200);
            }
            state.history = (state.history || []).filter((entry) => entry.job_id !== job.job_id);
            if (state.historyOffset > 0) {
                state.historyOffset = Math.max(0, state.historyOffset - 1);
            }
            if (Number.isFinite(state.historyTotal) && result && result.delete_result === 'ok') {
                state.historyTotal = Math.max(0, Number(state.historyTotal) - 1);
            }
            _historyRenderedKeys.delete(key);
            item.remove();

            if (!strip.querySelector('.history-item')) {
                refreshHistoryEmptyState();
            }
            updateHistoryCollapsedPreview(state.history || []);
            updateHistoryMeta();
            _refreshHistoryExpiryBadges();

            if (wasDisplayed) {
                const replacement = nextTile || prevTile;
                if (replacement && typeof replacement.onclick === 'function') {
                    replacement.onclick();
                    return;
                }
            }

            if (state.jobId === job.job_id) {
                state.jobId = null;
                if (state.selectedHistoryJobId === job.job_id) {
                    state.selectedHistoryJobId = null;
                    state.selectedHistoryJob = null;
                }
                state.tileMap = [];
                state.lastShownSettings = null;

                showEmpty();
                document.getElementById('progressArea').classList.add('hidden');
                document.getElementById('triviaCard').classList.add('hidden');
                clearSelectedSourcePhoto();

                const fileInput = document.getElementById('targetFile');
                if(fileInput) fileInput.value = "";
                refreshSettingsVisibility();
            } else if (state.selectedHistoryJobId === job.job_id) {
                state.selectedHistoryJobId = null;
                state.selectedHistoryJob = null;
            }

            document.dispatchEvent(new CustomEvent('history:fill'));

        } catch(err) {
            showToast('Delete failed', UI_COPY.history.deleteFailed(err.message), 0);
        }
    };

    tile.appendChild(delBtn);
    tile.appendChild(img);

    const meta = document.createElement('div');
    meta.className = 'meta';
    const name = document.createElement('div');
    name.className = 'name';
    name.textContent = timeStr;
    name.title = _formatHistoryTitle(job.timestamp) || timeStr;
    meta.appendChild(name);

    item.appendChild(tile);
    item.appendChild(meta);
    return item;
}

function _createHistoryCtaTile() {
    const item = document.createElement('div');
    item.className = 'trivia-thumb history-item history-create-cta';
    item.dataset.historyCta = 'create_mosaic';

    const tile = document.createElement('button');
    tile.type = 'button';
    tile.className = 'history-tile';
    tile.title = 'Create a new mosaic';
    tile.setAttribute('aria-label', 'Create a new mosaic');
    tile.innerHTML = '<span class="history-create-icon" aria-hidden="true">+</span><span class="history-create-copy">Create mosaic</span>';

    const meta = document.createElement('div');
    meta.className = 'meta';
    const name = document.createElement('div');
    name.className = 'name';
    name.textContent = 'Start new';
    meta.appendChild(name);

    const launchCreateStage = () => {
        openCreateMosaicForEdit({
            focus: false,
            step: 'main',
            source: 'history_create_cta',
            pushHistory: true,
        });
        requestWorkflowScroll('create', {
            source: 'history_create_cta',
            afterFrame: true,
            delayMs: 40,
            pushHistory: true,
        });
        API.logUiFlow('history', 'create_cta', 'ok', {
            loaded_count: Math.max(0, Number((state.history || []).length)),
        }).catch(() => {});
    };

    tile.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        launchCreateStage();
    });
    item.addEventListener('click', (event) => {
        if (event.target === tile) return;
        launchCreateStage();
    });

    item.appendChild(tile);
    item.appendChild(meta);
    return item;
}

function _ensureHistoryCreateCta(strip) {
    if (!strip) return null;
    let cta = strip.querySelector('.history-create-cta[data-history-cta="create_mosaic"]');
    if (!cta) {
        cta = _createHistoryCtaTile();
        strip.prepend(cta);
    }
    return cta;
}

export function renderHistory(jobs, clickHandler, opts = {}) {
    const strip = document.getElementById('historyStrip');
    const card = document.getElementById('historyCard');
    const { reset = false, prepend = false, selectJobId = null } = opts;
    if (!strip || !card) return;

    if (reset) {
        strip.innerHTML = "";
        _historyRenderedKeys = new Set();
    }

    const ctaTile = _ensureHistoryCreateCta(strip);

    if (selectJobId) {
        state.selectedHistoryJobId = selectJobId;
        state.selectedHistoryJob = (state.history || []).find((entry) => String(entry.job_id || '') === String(selectJobId)) || null;
        const selected = strip.querySelectorAll('.history-item.is-selected');
        selected.forEach((el) => el.classList.remove('is-selected'));
    }

    if ((!jobs || jobs.length === 0) && reset) {
        updateHistoryCollapsedPreview([]);
        refreshHistoryEmptyState();
        return;
    }

    for (const job of jobs) {
        const key = _historyKey(job);
        if (!key) continue;
        const encodedKey = encodeURIComponent(String(key));
        const safeJobId = String(job.job_id || '');

        const duplicatesByJobId = Array.from(strip.querySelectorAll('.history-item[data-job-id]'))
            .filter((node) => String(node.dataset.jobId || '') === safeJobId);
        for (const duplicate of duplicatesByJobId) {
            const duplicateKeyEncoded = String(duplicate.dataset.historyKey || '');
            if (duplicateKeyEncoded) {
                try {
                    _historyRenderedKeys.delete(decodeURIComponent(duplicateKeyEncoded));
                } catch (_e) {}
            }
            duplicate.remove();
        }

        const existing = strip.querySelector(`.history-item[data-history-key="${encodedKey}"]`);
        if (existing) {
            existing.remove();
            _historyRenderedKeys.delete(key);
        } else if (!prepend && _historyRenderedKeys.has(key) && duplicatesByJobId.length === 0) {
            continue;
        }

        const tile = _createHistoryTile(job, clickHandler, strip, card);
        if (prepend) {
            if (ctaTile && ctaTile.parentElement === strip) {
                strip.insertBefore(tile, ctaTile.nextSibling);
            } else {
                strip.prepend(tile);
            }
        } else {
            strip.appendChild(tile);
        }
        _historyRenderedKeys.add(key);
    }
    updateHistoryCollapsedPreview(state.history || []);
    refreshHistoryEmptyState();
    _refreshHistoryExpiryBadges();
}

export function updateHistoryMeta() {
    const subtitle = document.getElementById('historySubtitle');
    const meta = document.getElementById('historyMeta');
    const countEl = document.getElementById('historyCountLabel');
    const loadMore = document.getElementById('historyLoadMore');
    const loaded = (state.history || []).length;
    const totalVal = Number(state.historyTotal);
    const totalKnown = Number.isFinite(totalVal);
    const lifetimeVal = Number(state.historyLifetimeTotal);
    const hasLifetimeTotal = Number.isFinite(lifetimeVal) && lifetimeVal >= 0;
    const totalCreated = hasLifetimeTotal
        ? Math.max(
            loaded,
            Math.max(
                0,
                Math.floor(lifetimeVal),
                totalKnown ? Math.max(0, Math.floor(totalVal)) : 0,
            ),
        )
        : (totalKnown ? Math.max(loaded, Math.max(0, Math.floor(totalVal))) : loaded);

    if (subtitle) {
        subtitle.textContent = `You have created ${totalCreated} Mosaics. Explore your creations.`;
    }

    if (countEl) {
        countEl.textContent = '';
    }
    if (loadMore) {
        loadMore.disabled = true;
        loadMore.classList.add('hidden');
    }
    if (meta) {
        meta.classList.add('hidden');
    }
}
